import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { daysUntilCalendarDate } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import {
  AlertTriangle,
  Bell,
  ChevronDown,
  ChevronRight,
  Gauge,
  Loader2,
  MapPin,
  Shield,
  Users,
} from 'lucide-react';
import { useCollapsibleGroups } from '../hooks/useCollapsibleGroups';
import { CERT_TYPE_LABEL_MAP as CERT_TYPE_LABELS } from '../lib/certTypes';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../lib/load-failure';
import {
  ALERT_CLASS_BLURB,
  ALERT_CLASS_LABEL,
  CHASEABLE_ALERT_CLASSES,
  alertsOfClass,
  ageLabel,
  chaseableCount,
  describeAlertRow,
  type ChaseableAlertClass,
  type EquipmentAlertRow,
} from '../lib/equipmentAlerts';

/**
 * WHERE A FINISHED-JOB ROW GOES: the equipment list, filtered, not the asset.
 *
 * `/equipment/:id` has no return action, so a row that opened it was a dead
 * end — see it, then go find the list yourself. The list is where Return to
 * Yard lives, one asset or all of them (POST /equipment/bulk-return, API #603,
 * FE #563), and `EquipmentScreen` seeds its filter from `?status=`. So this is
 * "see it" to "select all, Return to Yard" in one hop.
 *
 * `left_on_site` rows keep going to the asset: that gear has a declared place,
 * and its remedy (Transfer to Job) is per asset, not a bulk action.
 *
 * WHAT THIS LINK IS TESTING, measured 2026-09-14 against production:
 *   - the `bulk_return` door had NEVER fired — zero `equipment_events` rows —
 *     eight days after it shipped, while Dutch and PAR held 28 undeclared
 *     finished-job rows between them, some 35 days old. Zero uses means the
 *     action was undiscoverable or unneeded; this link is what separates them.
 *     Re-read that event count before concluding either way.
 *   - there is NO HTTP request logging in the API, so whether anyone opens
 *     /alerts, or follows this link, cannot be measured. The only evidence
 *     available is the downstream write: `equipment_events` rows with
 *     door `bulk_return` or `equipment_return`, and who made them.
 */
const FINISHED_JOB_LIST = '/equipment?status=finished_job';

/**
 * ─── THE SEVERITY VOCABULARY ────────────────────────────────────────────────
 *
 * Three groups, one register, and a row from any of them has to answer the same
 * question at a glance: how bad is this one.
 *
 * TWO CHANNELS, NEVER ONE. The rail is the 4px edge you read down a scrolling
 * list; the badge is the word that says which state a row is in. An unknown
 * value gets a slate badge reading `—` and a TRANSPARENT rail — it keeps the
 * width so the columns line up and claims no colour, because a rail is a claim
 * about severity and a wrong claim in a colour is the silent kind of wrong.
 *
 * THE FILLS ARE `--fi-signal-*` AND THE INK ON THEM IS `--fi-signal-ink`. A
 * signal hue is a FILL to put dark ink on and is NOT an ink: it has no light
 * twin by design, so as a text colour it keeps its bright dark-mode value on a
 * white panel. fe#712 measured that exact mistake on /payroll/approvals — amber
 * 1.49, red 2.08, green 1.52 against a 4.5 floor. The page this one replaces
 * had it everywhere: `text-red-400`, `text-amber-400` and `text-green-400` as
 * ink, on ten-percent washes of themselves, on every heading, count, percentage
 * and age. None of it is here.
 *
 * EXPIRED AND EXPIRING ARE DIFFERENT STATES AND LOOK DIFFERENT, on all three
 * channels — hue, word, and the measure in the column beside them ("412 days
 * ago" against "37d left"). Before this change they shared a bucket the code
 * calls "expiring ≤90d" and differed only by the pill's wash. On the tenant
 * this was measured against, 47 of 56 crew rows were ALREADY EXPIRED, and one
 * of them by 659 days.
 */
interface Severity {
  label: string;
  fill: string;
  rail: string;
}

const SEV = {
  critical: { label: 'Critical', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  warning: { label: 'Warning', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
} as const;

/** The one shape for a value the screen could not classify. Never a fallback. */
const UNKNOWN_SEVERITY: Severity = {
  label: '—',
  fill: 'bg-fi-signal-slate',
  rail: 'border-l-transparent',
};

function SeverityBadge({ severity }: { severity: Severity }) {
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${severity.fill}`}
    >
      {severity.label}
    </span>
  );
}

/**
 * The gear classes, keyed on every member of `ChaseableAlertClass` — no
 * fallback branch that guesses. `finished_job` is the ABSENCE of a claim and
 * takes the red; `left_on_site` is a POSITIVE claim somebody made and takes the
 * amber. See lib/equipmentAlerts for why they are not one list.
 */
const GEAR_SEVERITY: Record<ChaseableAlertClass, Severity> = {
  finished_job: { label: 'Undeclared', fill: SEV.critical.fill, rail: SEV.critical.rail },
  left_on_site: { label: 'On Location', fill: SEV.warning.fill, rail: SEV.warning.rail },
};

/**
 * Cert usage, banded on the percentage the API returned.
 *
 * NOT EXHAUSTIVE OVER AN ENUM, because this is a continuous figure rather than
 * a state — so the bands are written as an ordered ladder with an explicit
 * absent branch at the top rather than as a map with a fallback. An absent
 * percentage is a fact about the DATA (nobody has counted this asset) and can
 * never be a band.
 *
 * ITS ABSENT BRANCH IS CURRENTLY UNREACHABLE THROUGH THE LIST, AND THAT IS A
 * REPORTED DEFECT RATHER THAN DEAD CODE. `certAlerts` filters on
 * `(e.percentUsed ?? 0) >= 75`, so a row whose counter has never been recorded
 * is dropped before it ever gets here — the branch is structurally cold, which
 * is the shape CLAUDE.md names when it asks what a figure would read if the
 * thing you fear were true. The branch stays because the filter is the thing
 * that should change and this re-skin may not change which rows the page
 * shows; it is pinned by a direct unit test rather than through the register,
 * because a fixture that cannot reach an assertion tests nothing (fe#603).
 *
 * OVER LIMIT IS ITS OWN WORD. A unit past 100% of its certification allowance
 * is not "nearly due" — it is out of cert and should not go out — and folding
 * it into the same pill as an 91% unit loses the only distinction on this row
 * that changes what anybody does about it.
 */
export function certSeverity(pct: number | null | undefined): Severity {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return UNKNOWN_SEVERITY;
  if (pct >= 100) return { label: 'Over Limit', fill: SEV.critical.fill, rail: SEV.critical.rail };
  if (pct >= 90) return { label: 'Critical', fill: SEV.critical.fill, rail: SEV.critical.rail };
  return { label: 'Approaching', fill: SEV.warning.fill, rail: SEV.warning.rail };
}

/** The crew split, and it is the whole point of this column. */
const CREW_SEVERITY: Record<'expired' | 'warning', Severity> = {
  expired: { label: 'Expired', fill: SEV.critical.fill, rail: SEV.critical.rail },
  warning: { label: 'Expiring', fill: SEV.warning.fill, rail: SEV.warning.rail },
};

/**
 * How far past, or how far off. Absent is not zero and expired is not expiring.
 *
 * `daysLeft` is the SIGNED calendar-day difference, kept signed. The screen this
 * replaces clamped every expired row to `daysLeft: 0` and printed the word
 * EXPIRED, which threw away the magnitude — so a cert 6 days out and one 659
 * days out were the same row. The magnitude is what tells a reader which of 47
 * expired certs to chase first.
 */
function crewMeasure(daysLeft: number, status: 'expired' | 'warning'): string {
  if (status === 'expired') {
    const past = Math.abs(daysLeft);
    return `${past} day${past === 1 ? '' : 's'} ago`;
  }
  if (daysLeft === 0) return 'today';
  return `${daysLeft}d left`;
}

/** A percentage, or an em dash. `pct ?? 0` would read 0% as "nothing to worry about". */
function pctLabel(pct: number | null | undefined): string {
  return pct === null || pct === undefined || !Number.isFinite(pct) ? '—' : `${Math.round(pct)}%`;
}

function getInitials(firstName?: string, lastName?: string) {
  return ((firstName?.[0] || '') + (lastName?.[0] || '')).toUpperCase() || '?';
}

/**
 * One group's heading — a FULL-WIDTH ROW, not a record.
 *
 * It carries the same `border-l-4 border-l-transparent` as the heading row: the
 * rail width keeps the columns under it aligned, and the colour is absent
 * because a group is not a record and must not look like it has a severity. A
 * coloured rail here would read as a severity for the whole group.
 *
 * ─── THE ROW IS STILL NOT A TARGET. THE CHEVRON IS. ─────────────────────────
 *
 * Collapse does NOT arrive by giving the `<tr>` a `tabIndex`, an `onClick` and
 * a selection tint. That is the shape CLAUDE.md forbids, and the reason is
 * unchanged by making the group collapsible: a header styled like a row target
 * reads as a record, and a record on this page carries a severity. The rule is
 * about what the row CLAIMS, not about whether anything on it is clickable.
 *
 * So the toggle is a `<button>` INSIDE the header cell, wrapping the icon,
 * label and count — the affordance is the chevron and the words beside it, and
 * the row around it keeps no interactive attributes at all. This is exactly
 * Equipment's type header (fe#698), which has been collapsible since it
 * shipped; the idiom is being reused here, not invented.
 *
 * WHY THE BUTTON DOES NOT SWALLOW THE WHOLE CELL, which is where this differs
 * from Equipment: the finished-job group carries a `<Link>` to its remedy, and
 * an anchor inside a button is invalid HTML and unreachable by keyboard in
 * practice. The button therefore claims the label and count only, and the blurb
 * and the action sit beside it as siblings. Equipment's header holds nothing
 * but its toggle, so it can afford the wider target.
 *
 * WHAT A SCREEN READER GETS: `aria-expanded` on the button, and an accessible
 * name that is the label AND the count — "Crew Certifications & Licences, 56
 * alerts, collapsed" is the whole state in one utterance. The count is not
 * decoration here; it is the reason to open the group, so it has to be in the
 * name rather than only in a visual sibling. `aria-controls` points at the row
 * group, so the relationship survives the rows themselves being unrendered.
 */
function GroupHeader({
  label,
  blurb,
  count,
  icon,
  action,
  collapsed,
  onToggle,
  controls,
}: {
  label: string;
  blurb: string;
  count: number;
  icon: React.ReactNode;
  action?: React.ReactNode;
  collapsed: boolean;
  onToggle: () => void;
  controls: string;
}) {
  return (
    <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent bg-fieldiq-dark/40">
      <td colSpan={5} className="px-4 py-2.5">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            aria-controls={controls}
            /* THE NAME CARRIES THE COUNT. The visible number beside it is
               `aria-hidden` for the same reason the separator is: read as a
               bare text node it would double the figure in the utterance. */
            aria-label={`${label}, ${count} ${count === 1 ? 'alert' : 'alerts'}`}
            className="-mx-1.5 flex shrink-0 items-baseline gap-x-2 rounded-fi-pill px-1.5 py-0.5 text-left outline-none transition hover:bg-fieldiq-dark/70 focus-visible:bg-fi-amber/10"
          >
            <span className="shrink-0 self-center text-fieldiq-muted" aria-hidden="true">
              {collapsed ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
            </span>
            <span className="shrink-0 self-center text-fieldiq-muted" aria-hidden="true">
              {icon}
            </span>
            <h3 className="text-xs font-black uppercase tracking-[0.1em] text-white">{label}</h3>
            {/* A BARE NUMBER, not "(3)". `getByText('1')` is an exact match on
                the element's own text nodes, and the register's group counts are
                asserted that way. The separator is a sibling, not a wrapper.

                IT IS RENDERED IN BOTH STATES, and that is the point of the
                whole change: a collapsed group that dropped its count would
                hide the size of the problem, and the size is the reason to
                open it. */}
            <span aria-hidden="true" className="text-fieldiq-muted">·</span>
            <span aria-hidden="true" className="text-xs tabular-nums text-fieldiq-muted">
              {count}
            </span>
          </button>
          <p className="text-xs text-fieldiq-muted">{blurb}</p>
          {action}
        </div>
      </td>
    </tr>
  );
}

/**
 * A register row. Every one of them is a link to the record it is ABOUT, which
 * is the change this re-skin exists to make: the page used to be a list of
 * sentences, and the remedy for each one lived somewhere the reader had to go
 * find. There is no "view" button — the row IS the link, per the register idiom
 * (CLAUDE.md), so the whole 900px-wide target opens the record.
 *
 * `role="button"` is deliberately NOT set. These rows hold no inline action
 * buttons today, so Quotes' shape would be legal — but the finished-job group
 * header holds a link, and the next group that grows a per-row action would hit
 * fe#647's trap (an explicit button role makes children presentational). The
 * keydown guard below is Rate Sheets' and costs nothing while it is unneeded.
 */
function AlertRow({
  subject,
  detail,
  measure,
  measureTitle,
  measureMuted,
  severity,
  leading,
  onOpen,
}: {
  subject: string;
  detail: string;
  measure: string;
  measureTitle?: string;
  measureMuted?: boolean;
  severity: Severity;
  leading?: React.ReactNode;
  onOpen: () => void;
}) {
  const keys = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    // A key on an inner control is that control's, never the row's.
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };
  return (
    <tr
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={keys}
      className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${severity.rail}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {leading}
          <span className="text-sm font-bold text-white">{subject}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-fieldiq-text">{detail}</td>
      <td
        className={`whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums ${
          measureMuted ? 'text-fieldiq-muted' : 'font-extrabold text-white'
        }`}
        title={measureTitle}
      >
        {measure}
      </td>
      <td className="px-4 py-3">
        <SeverityBadge severity={severity} />
      </td>
      <td className="px-4 py-3">
        <ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" />
      </td>
    </tr>
  );
}

/**
 * The register's group ids. Stable strings, not the visible labels: they are
 * what the session store is keyed on, so renaming a heading must not silently
 * drop somebody's collapsed state mid-session.
 */
const GROUP_ID = {
  finished_job: 'finished_job',
  left_on_site: 'left_on_site',
  certs: 'equipment_cert_usage',
  crew: 'crew_certifications',
} as const;

/** The row group a header's `aria-controls` points at. */
const groupBodyId = (key: string) => `alerts-group-${key}`;

export default function AlertsScreen() {
  const navigate = useNavigate();
  // The TENANT's zone: whose today a cert's days-left is counted from.
  const timeZone = useTenantTimeZone();

  /**
   * ─── DEFAULT: EVERY GROUP OPEN. THE DECISION, AND WHY IT IS NOT A SETTING ──
   *
   * The brief offered a threshold — a group over N rows starts collapsed —
   * and it is the wrong default here for two separate reasons.
   *
   * 1. THIS IS THE PAGE WHOSE DOCUMENTED FAILURE IS UNDER-REPORTING. It
   *    rendered a green shield over "All Clear" while it could not reach the
   *    server (fe#705, and the docblock on `sources` below). A default that
   *    starts by hiding the largest set of alerts is the same instinct one
   *    notch down: the reader arrives NOT KNOWING what is wrong, which is the
   *    whole reason they opened it. Equipment can afford to start collapsed
   *    (fe#698) because a register is browsed by someone who already knows
   *    which type they want; nobody arrives here knowing which group to open.
   *
   * 2. A THRESHOLD IS AN UNLEARNABLE DEFAULT. N has no principled value — any
   *    number is a guess — and the page would render differently per tenant
   *    and differently on consecutive days as a count crosses it. A reader
   *    cannot form an expectation about a layout that changes with the data,
   *    so the first thing they would learn is not to trust what is on screen.
   *    On the measured estate it would also collapse precisely the group with
   *    56 of the 61 alerts, which is a strange thing for an alerts page to do
   *    on its own initiative.
   *
   * WHAT SOLVES THE STATED PROBLEM INSTEAD is the pair below it: Collapse all
   * is one click, and the choice is remembered for the session. The complaint
   * is that Crew Certifications BURIES the other groups while you are working
   * — a browsing problem, and the browsing session is exactly the scope the
   * memory has. Shut it once and it stays shut until the tab closes, without
   * the app having decided on anyone's behalf what they did not need to see.
   */
  const { isCollapsed, toggleGroup, expandAll, collapseAll } = useCollapsibleGroups({
    defaultCollapsed: false,
    storageKey: 'alerts:groups:collapsed',
  });

  // ── Gear that is out and owes somebody an answer ───────────────────────────
  //
  // GET /dispatch/equipment-alerts, not a client-side filter over the whole
  // equipment table. The endpoint was built for exactly this question and had
  // no caller; the browser-side filter could see an asset's STATUS and nothing
  // else — not how long it has been sitting, not who last touched it, and not
  // the finished_job class at all.
  const eqQuery = useQuery({
    queryKey: ['equipment-alerts'],
    queryFn: () =>
      api.dispatch.equipmentAlerts().then(r => (r.data?.data ?? []) as EquipmentAlertRow[]),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: alertsData, isLoading: eqLoading } = eqQuery;
  const equipmentAlerts: EquipmentAlertRow[] = alertsData ?? [];

  // Equipment cert usage
  const certQuery = useQuery({
    queryKey: ['cert-summary-home'],
    queryFn: () => api.equipment.certSummary().then(r => r.data.data ?? r.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: certSummaryData, isLoading: certLoading } = certQuery;
  const certItems: any[] = certSummaryData ?? [];
  /**
   * `(e.percentUsed ?? 0) >= 75` — UNCHANGED, AND IT IS A REPORTED DEFECT.
   *
   * The API returns `percentUsed: null` for an asset whose counter has never
   * been recorded, deliberately and with a docblock saying so. The `?? 0` here
   * turns that into 0 and drops the row off this list: an asset nobody has
   * counted is silently absent from the page whose job is to say what needs
   * attention. Measured against production 2026-09-20: 31 such rows, Dash 18
   * and TNT 13, out of 158 cert-tracked assets estate-wide.
   *
   * It is left alone on purpose. Fixing it changes WHICH ROWS THE PAGE SHOWS,
   * which is a behaviour change this re-skin may not make — see the PR body.
   */
  const certAlerts = certItems.filter((e: any) => (e.percentUsed ?? 0) >= 75);

  // Team members — crew cert + DL alerts
  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => api.users.list().then(r => r.data.data ?? r.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: usersData, isLoading: usersLoading } = usersQuery;
  const users: any[] = usersData ?? [];

  const now = new Date(); // one instant for every row; the day is read in the tenant's zone
  /**
   * `daysLeft` IS SIGNED HERE and was not before. An expired row used to be
   * written `daysLeft: 0`, which collapsed 47 expired certs onto one another —
   * see `crewMeasure`. Nothing else about this derivation changed: the same
   * two sources (`certifications[].expiryDate` and `driversLicenceExpiry`), the
   * same ≤90-day window, the same `daysUntilCalendarDate` (a calendar day is
   * not an instant), and a cert with no expiry is still skipped rather than
   * guessed at.
   */
  const crewAlerts: {
    userId: string;
    name: string;
    initials: string;
    label: string;
    daysLeft: number;
    status: 'expired' | 'warning';
  }[] = [];

  for (const member of users) {
    const firstName = member.firstName || member.first_name || '';
    const lastName = member.lastName || member.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const initials = getInitials(firstName, lastName);

    // Check certifications
    const certs = member.certifications || [];
    for (const cert of certs) {
      if (!cert.expiryDate) continue;
      const daysLeft = daysUntilCalendarDate(cert.expiryDate, timeZone, now);
      if (daysLeft == null) continue;
      const certLabel = CERT_TYPE_LABELS[cert.certificationType] || CERT_TYPE_LABELS[cert.type] || cert.name || cert.certificationType || cert.type || 'Cert';

      if (daysLeft < 0) {
        crewAlerts.push({ userId: member.id, name: fullName, initials, label: certLabel, daysLeft, status: 'expired' });
      } else if (daysLeft <= 90) {
        crewAlerts.push({ userId: member.id, name: fullName, initials, label: certLabel, daysLeft, status: 'warning' });
      }
    }

    // Check driver's licence
    const dlExpiry = member.driversLicenceExpiry || member.drivers_licence_expiry;
    const daysLeft = daysUntilCalendarDate(dlExpiry, timeZone, now);
    if (daysLeft != null) {
      if (daysLeft < 0) {
        crewAlerts.push({ userId: member.id, name: fullName, initials, label: "Driver's Licence", daysLeft, status: 'expired' });
      } else if (daysLeft <= 90) {
        crewAlerts.push({ userId: member.id, name: fullName, initials, label: "Driver's Licence", daysLeft, status: 'warning' });
      }
    }
  }

  // Expired first and longest-expired first within that, then the soonest to
  // expire. `daysLeft` is signed, so one ascending sort does both halves.
  crewAlerts.sort((a, b) => a.daysLeft - b.daysLeft);

  const expiredCrew = crewAlerts.filter(a => a.status === 'expired').length;

  const isLoading = eqLoading || certLoading || usersLoading;

  /**
   * THIS SCREEN'S WHOLE JOB IS TO SAY SOMETHING IS WRONG, AND IT USED TO SAY
   * "ALL CLEAR" WHEN IT COULD NOT DO THAT JOB.
   *
   * All three queries were destructured `{ data, isLoading }`. A refusal or a
   * dead connection left every `data` undefined, each `?? []` turned that into
   * an empty list, `totalAlerts` came out 0, and the page rendered a green
   * shield over "No alerts at this time. Equipment and crew certifications are
   * up to date." A screen that could not reach the server knows nothing about
   * anybody's certifications, and that sentence is a claim it has no basis for
   * — on the one page somebody opens specifically to find out.
   *
   * THREE SOURCES, REPORTED SEPARATELY, and that is the part worth keeping.
   * A single "something went wrong" banner would be a second lie in the other
   * direction: if the users list is refused but equipment answered, the gear
   * section IS complete and the reader should still act on it. So each source
   * says for itself whether it arrived, and only the ones that did contribute
   * to the count.
   *
   * fe#705 shipped that. NOTHING IN THIS RE-SKIN MAY REGRESS IT — the band's
   * figures are per-source for the same reason the register's sections are.
   */
  const sources = [
    { key: 'equipment', subject: 'equipment alerts', query: eqQuery, count: chaseableCount(equipmentAlerts) },
    { key: 'certs', subject: 'equipment certification usage', query: certQuery, count: certAlerts.length },
    { key: 'crew', subject: 'crew certifications', query: usersQuery, count: crewAlerts.length },
  ] as const;

  // `queryLoadFailure`, not `s.query.error`: a query waiting to retry holds
  // its failure on `failureReason` and reports `error: null`. See that
  // function's docblock -- reading `error` alone left this screen saying "All
  // Clear" over three 500s.
  const failedSources = sources
    .map((s) => ({ ...s, failure: queryLoadFailure(s.query) }))
    .filter((s) => s.failure !== null);

  const eqFailed = queryLoadFailure(eqQuery) !== null;
  const certFailed = queryLoadFailure(certQuery) !== null;
  const crewFailed = queryLoadFailure(usersQuery) !== null;

  // Only the sources that answered. A count over a partial estate is still a
  // real count of what was found — it is the claim of COMPLETENESS that has to
  // go, and that is what `failedSources` carries.
  const totalAlerts = sources
    .filter((s) => queryLoadFailure(s.query) === null)
    .reduce((sum, s) => sum + s.count, 0);

  const hasRows =
    chaseableCount(equipmentAlerts) > 0 || certAlerts.length > 0 || crewAlerts.length > 0;

  /**
   * The groups that are actually ON SCREEN, which is what the all-or-nothing
   * control has to reason about. A group with no rows is not rendered at all,
   * and asking `isCollapsed` about one would let an absent section decide the
   * button's word — "Expand all" over a register where everything visible is
   * already open.
   */
  const renderedGroupIds: string[] = [
    ...CHASEABLE_ALERT_CLASSES.filter(
      (cls: ChaseableAlertClass) => alertsOfClass(equipmentAlerts, cls).length > 0
    ).map((cls: ChaseableAlertClass) => GROUP_ID[cls]),
    ...(certAlerts.length > 0 ? [GROUP_ID.certs] : []),
    ...(crewAlerts.length > 0 ? [GROUP_ID.crew] : []),
  ];
  const allGroupsCollapsed =
    renderedGroupIds.length > 0 && renderedGroupIds.every((id) => isCollapsed(id));

  return (
    <div data-testid="alerts-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global <Header />. Pages never do. */}
      <PageHeaderBand
        eyebrow="Estate exceptions"
        title="Alerts"
        onBack={() => navigate(-1)}
        backLabel="Back"
        /* FOUR FIGURES, ALL FROM THE THREE QUERIES ALREADY ON THIS PAGE — no
           fourth request, and nothing recomputed that an endpoint returns. The
           sub-lines are STATIC copy naming what each figure counts; none of
           them is a second figure.

           EACH ONE IS GATED ON ITS OWN SOURCE, which is fe#705's rule carried
           into the band. A source that did not arrive contributes an em dash,
           not a zero, and the total goes to a dash the moment ANY of the three
           is missing — a partial total is a complete-sounding sentence and a
           reader acts on it.

           MEASURED AGAINST THE CIRCLE, not assumed, and the third stat is why
           the wording is what it is.

           THE THIRD LABEL HAS A ~70px CEILING (fe#698) because between 1024 and
           1048 the circle's edge crosses that column. "Cert Usage" measured
           84.9px — over it — so it was shortened to "Certs" (44.1px) and the
           subject moved into the sub-line, which sits where there is no circle.
           That is Equipment's own precedent: shorten the STAT, leave the page's
           names alone. The full sweep clears the longer wording too, but a
           fresh measurement of mine is not a reason to spend a documented
           limit.

           THE FOURTH SUB-LINE is 95.4px / 16 characters — SHORTER than the
           reference page's own (Quotes, 110px / 18) — against fe#712's finding
           of a 26-character one crossing at 1256–1432.

           The sweep: 1024→2560 every 8px, BOTH themes, scored on real
           composited pixels with the ink's own alpha. Worst 4.86 dark / 5.03
           light, nothing below 4.5. Numbers and the control in the PR body. */
        stats={[
          {
            icon: <Bell className="size-4" aria-hidden="true" />,
            label: 'Open Alerts',
            sub: 'Gear, certs and crew',
            tone: 'accent',
            loading: isLoading,
            value: (
              <span data-testid="alerts-total">
                {failedSources.length > 0 ? '—' : totalAlerts}
              </span>
            ),
          },
          {
            icon: <MapPin className="size-4" aria-hidden="true" />,
            label: 'Gear Out',
            sub: 'Nobody has said',
            tone: 'neutral',
            loading: eqLoading,
            value: eqFailed ? '—' : chaseableCount(equipmentAlerts),
          },
          {
            icon: <Gauge className="size-4" aria-hidden="true" />,
            label: 'Certs',
            sub: 'Equipment, 75%+',
            tone: 'info',
            loading: certLoading,
            value: certFailed ? '—' : certAlerts.length,
          },
          {
            icon: <Users className="size-4" aria-hidden="true" />,
            label: 'Crew Expired',
            sub: 'Already past due',
            tone: 'accent',
            loading: usersLoading,
            value: crewFailed ? '—' : expiredCrew,
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* One notice per source that did not arrive, named, so a reader can
              tell which half of the page is trustworthy. They sit ABOVE the
              register rather than inside it because the register still holds
              the groups that DID load — the notice is about a missing section,
              not about the list it is standing next to. */}
          {failedSources.map((s) => (
            <div
              key={s.key}
              className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card"
            >
              <LoadFailureNotice
                error={queryFailureError(s.query)}
                kind={s.failure ?? undefined}
                subject={s.subject}
              />
            </div>
          ))}

          {/* ── THE REGISTER ────────────────────────────────────────────────
              ONE PANEL, ONE TABLE, FULL WIDTH, three groups of rows under
              full-width group headings. There is no detail pane: a row's detail
              is the record it is about, and the row opens it.

              NO STACKED-CARD BRANCH BELOW `md`, which is a deliberate deviation
              from the register idiom and the only one here. A second render of
              every row would double this page in the DOM and in the
              accessibility tree, and the rows are heterogeneous — three groups
              with three different measures — so the card would be a fourth
              layout to keep in step with the other three. The table scrolls
              horizontally inside its own container instead, which is the
              mobile rule this page needs. */}
          <section
            data-testid="alerts-register"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
              <div>
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                  Exception register
                </p>
                <h2 className="mt-1 text-base font-extrabold tracking-tight text-white">
                  What needs chasing
                </h2>
              </div>
              {/* THE ONE-CLICK ESCAPE THAT MAKES "ALL OPEN" THE RIGHT DEFAULT.
                  It is rendered only when there are rows to act on: a control
                  that shuts nothing reads as broken rather than as empty, which
                  is Equipment's own reasoning for disabling its select-all.

                  It says COLLAPSE ALL until every group is shut and EXPAND ALL
                  after — it reports the action, not the state, because a
                  toggle labelled with the state it is in is the commonest way
                  to build a button nobody can predict. */}
              {hasRows && !isLoading && (
                <button
                  type="button"
                  onClick={() => (allGroupsCollapsed ? expandAll() : collapseAll())}
                  className="rounded-fi-pill border border-fieldiq-border bg-fieldiq-dark px-3 py-1.5 text-xs font-bold text-fieldiq-text outline-none transition hover:bg-fieldiq-dark/70 focus-visible:bg-fi-amber/10"
                >
                  {allGroupsCollapsed ? 'Expand all' : 'Collapse all'}
                </button>
              )}
            </header>

            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" />
              </div>
            ) : hasRows ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-left">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                    {/* The heading row carries the same 4px in no colour at
                        all, so the columns line up with the rails below it. */}
                    <tr className="border-l-4 border-l-transparent">
                      <th scope="col" className="px-4 py-3">Subject</th>
                      <th scope="col" className="px-4 py-3">Detail</th>
                      <th scope="col" className="px-4 py-3 text-right">Measure</th>
                      <th scope="col" className="px-4 py-3">Severity</th>
                      <th scope="col" className="w-[52px] px-4 py-3" />
                    </tr>
                  </thead>

                  {/* ── Gear that is out and owes an answer ─────────────────
                      TWO CLASSES, ONE PROBLEM, RENDERED DISTINCTLY. To dispatch
                      these are the same thing — gear that is out and nobody has
                      declared what happens next — so they are one register.
                      They are not the same FACT, so they keep their own
                      headings, blurbs and severities:

                        finished_job   the ABSENCE of a claim. The job ended and
                                       nobody said where the asset went. Red.
                        left_on_site   a POSITIVE CLAIM. A hand said it stays on
                                       the lease. Amber.

                      `maintenance` is deliberately not here — gear in the shop
                      is gear somebody HAS declared the next step for. See
                      lib/equipmentAlerts.

                      THE GROUP IS A LANDMARK. `role="region"` on the row group
                      names a section a screen reader — and a test — can address
                      without walking the DOM. It replaces the implicit
                      `rowgroup`, which names nothing and which nothing here
                      relies on; the rows and cells keep their own semantics. */}
                  {CHASEABLE_ALERT_CLASSES.map((cls: ChaseableAlertClass) => {
                    const rows = alertsOfClass(equipmentAlerts, cls);
                    if (rows.length === 0) return null;
                    const isAbsence = cls === 'finished_job';
                    const gid = GROUP_ID[cls];
                    const shut = isCollapsed(gid);
                    return (
                      <tbody
                        key={cls}
                        id={groupBodyId(gid)}
                        role="region"
                        aria-label={ALERT_CLASS_LABEL[cls]}
                      >
                        <GroupHeader
                          label={ALERT_CLASS_LABEL[cls]}
                          blurb={ALERT_CLASS_BLURB[cls]}
                          count={rows.length}
                          collapsed={shut}
                          onToggle={() => toggleGroup(gid)}
                          controls={groupBodyId(gid)}
                          icon={isAbsence ? <AlertTriangle className="size-4" /> : <MapPin className="size-4" />}
                          action={
                            isAbsence ? (
                              <Link
                                to={FINISHED_JOB_LIST}
                                className="whitespace-nowrap text-xs font-bold text-fieldiq-accent-signal hover:underline"
                              >
                                Return to yard from Equipment →
                              </Link>
                            ) : undefined
                          }
                        />
                        {!shut && rows.map((eq) => (
                          <AlertRow
                            key={eq.id}
                            subject={eq.name || eq.serial_number || 'Unknown'}
                            detail={describeAlertRow(eq)}
                            /* THE NUMBER THAT MATTERS, and it may be unknown.
                               days_since_job_closed is not coalesced by the
                               API: 87 completed jobs carry a NULL completed_at.
                               An unknown age rendered as `0 days` would read as
                               "closed today", so it renders as an em dash and
                               sorts FIRST. */
                            measure={ageLabel(eq.days_since_job_closed)}
                            measureMuted={
                              eq.days_since_job_closed === null || eq.days_since_job_closed === undefined
                            }
                            measureTitle={
                              eq.days_since_job_closed === null || eq.days_since_job_closed === undefined
                                ? 'The job carries no completion time, so the age is unknown'
                                : 'Days since the job closed'
                            }
                            severity={GEAR_SEVERITY[cls]}
                            onOpen={() => navigate(isAbsence ? FINISHED_JOB_LIST : `/equipment/${eq.id}`)}
                          />
                        ))}
                      </tbody>
                    );
                  })}

                  {/* ── Equipment cert usage ────────────────────────────────
                      THE ROW OPENS THE ASSET. It used to open /equipment/certs,
                      the whole cert list, which is where the reader already is
                      in spirit — a list of units at 75% or over. The record the
                      row is ABOUT is the unit, and its cert dates, adjustments
                      and history live on it. A row with no id opens nothing
                      rather than /equipment/undefined. */}
                  {certAlerts.length > 0 && (
                    <tbody
                      id={groupBodyId(GROUP_ID.certs)}
                      role="region"
                      aria-label="Equipment Cert Usage"
                    >
                      <GroupHeader
                        label="Equipment Cert Usage"
                        blurb="Working days used against the unit's certification limit."
                        count={certAlerts.length}
                        collapsed={isCollapsed(GROUP_ID.certs)}
                        onToggle={() => toggleGroup(GROUP_ID.certs)}
                        controls={groupBodyId(GROUP_ID.certs)}
                        icon={<Gauge className="size-4" />}
                      />
                      {!isCollapsed(GROUP_ID.certs) && [...certAlerts]
                        .sort((a: any, b: any) => (b.percentUsed ?? 0) - (a.percentUsed ?? 0))
                        .map((cert: any) => {
                          const assetId = cert.id || cert.equipmentId;
                          return (
                            <AlertRow
                              key={assetId || `${cert.equipmentName}-${cert.percentUsed}`}
                              subject={cert.equipmentName || cert.name || 'Equipment'}
                              detail={cert.certType || cert.certName || cert.certDescription || 'Certification'}
                              measure={pctLabel(cert.percentUsed)}
                              measureMuted={cert.percentUsed === null || cert.percentUsed === undefined}
                              measureTitle="Working days used, as a share of the unit's cert day limit"
                              severity={certSeverity(cert.percentUsed)}
                              onOpen={() => { if (assetId) navigate(`/equipment/${assetId}`); }}
                            />
                          );
                        })}
                    </tbody>
                  )}

                  {/* ── Crew certifications and licences ────────────────────
                      THE ROW OPENS THE PERSON. Expired and expiring sit in one
                      group and are told apart by hue, word and measure — see
                      the severity docblock at the top of this file. */}
                  {crewAlerts.length > 0 && (
                    <tbody
                      id={groupBodyId(GROUP_ID.crew)}
                      role="region"
                      aria-label="Crew Certifications & Licences"
                    >
                      <GroupHeader
                        label="Crew Certifications & Licences"
                        blurb="Expired, and expiring within 90 days."
                        count={crewAlerts.length}
                        collapsed={isCollapsed(GROUP_ID.crew)}
                        onToggle={() => toggleGroup(GROUP_ID.crew)}
                        controls={groupBodyId(GROUP_ID.crew)}
                        icon={<Shield className="size-4" />}
                      />
                      {!isCollapsed(GROUP_ID.crew) && crewAlerts.map((alert, idx) => (
                        <AlertRow
                          key={`${alert.userId}-${alert.label}-${idx}`}
                          subject={alert.name}
                          detail={alert.label}
                          measure={crewMeasure(alert.daysLeft, alert.status)}
                          measureTitle={
                            alert.status === 'expired'
                              ? 'Calendar days since this expired'
                              : 'Calendar days until this expires'
                          }
                          severity={CREW_SEVERITY[alert.status]}
                          leading={
                            <span className="grid size-8 shrink-0 place-items-center rounded-fi-pill bg-fieldiq-dark text-xs font-bold text-fieldiq-text">
                              {alert.initials}
                            </span>
                          }
                          onOpen={() => navigate(`/team/${alert.userId}`)}
                        />
                      ))}
                    </tbody>
                  )}
                </table>
              </div>
            ) : failedSources.length > 0 ? (
              /* Every source that answered was empty, and at least one did not
                 answer at all. There is nothing to list and nothing to assure:
                 the notices above are the whole content. */
              <div className="px-4 py-12 text-center">
                <p className="text-sm text-fieldiq-text">
                  Nothing found in the sources that answered.
                </p>
                <p className="mt-1 text-xs text-fieldiq-muted">
                  That is not the same as nothing being wrong.
                </p>
              </div>
            ) : (
              /* All Clear — gated on every source having answered. It is the
                 strongest assurance this app makes anywhere and it is only true
                 when all three lists arrived and all three were empty. */
              <div className="px-4 py-16 text-center">
                <Shield className="mx-auto mb-3 size-10 text-fi-green" aria-hidden="true" />
                <h3 className="text-lg font-extrabold tracking-tight text-white">All Clear</h3>
                <p className="mt-1 text-sm text-fieldiq-text">
                  No alerts at this time. Equipment and crew certifications are up to date.
                </p>
              </div>
            )}

            {/* THE FOOTER CARRIES A SCOPE, which is what earns a footer its
                place (fe#711): how much of the estate this page could see. On a
                clean load it is a count of everything found; with a source
                missing it says so and the figure above goes to a dash.

                "0 total alerts requiring attention" is deliberately still a
                sentence on a clean empty load — a real zero is a MEASUREMENT
                when all three sources answered, and it has to keep reading as
                one, or the fix for the false assurance has become a permanent
                shrug. */}
            {(!isLoading || failedSources.length > 0) && (
              <p
                data-testid="alerts-scope"
                className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted"
              >
                {failedSources.length > 0
                  ? `Total unknown — ${totalAlerts} found so far, and ${failedSources.length} of ${sources.length} sources did not load`
                  : `${totalAlerts} total alerts requiring attention, across all ${sources.length} sources`}
              </p>
            )}
          </section>
        </div>
      </section>
    </div>
  );
}
